import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Page from '../../Components/Page/BookingPage';
import styles from './policy.module.css';

const sections = [
    {
        id: 'privacy',
        title: 'Privacy Policy',
        intro: 'Nails By Astraea respects your privacy. This policy explains how your contact information is collected and used, including how text messages (SMS) are handled.',
        groups: [
            {
                heading: 'Information Collected',
                items: [
                    'When you book an appointment, I collect your name, phone number, email address, and appointment details.',
                    'Card details are collected and stored securely by Stripe. Nails By Astraea never stores your full card number.',
                ],
            },
            {
                heading: 'How Your Information Is Used',
                items: [
                    'Your information is used only to manage your appointments, including confirmations, reminders, schedule changes, and responses to your questions.',
                ],
            },
            {
                heading: 'Text Messages (SMS)',
                items: [
                    'By providing your phone number when booking, you agree to receive appointment-related text messages from Nails By Astraea.',
                    'Message frequency varies based on your appointments. Message and data rates may apply.',
                    'Reply STOP at any time to unsubscribe from text messages. You will receive one final message confirming your opt-out.',
                    'Reply HELP for assistance, or contact astraea@nailsbyastraea.com.',
                ],
            },
            {
                heading: 'Information Sharing',
                items: [
                    'Mobile numbers and text messaging consent are never sold, rented, or shared with third parties or affiliates for marketing or promotional purposes.',
                    'Information is shared only with service providers required to operate bookings, such as payment processing and messaging providers.',
                ],
            },
            {
                heading: 'Contact',
                items: [
                    'For questions about this policy or to request changes to your information, email astraea@nailsbyastraea.com.',
                ],
            },
        ],
    },
    {
        id: 'payment',
        title: 'Payment, Cancellation & Reschedule Policy',
        groups: [
            {
                heading: 'Appointments',
                items: [
                    'All services are by appointment only and must be booked through this website. Walk-ins are not accepted.',
                    'A $20 deposit is required when the appointment total exceeds $20. Appointments totaling $20 or less require no deposit; the full balance is collected after the appointment.',
                    'You are welcome to bring up to 2 guests to your appointment.',
                    'Please arrive with bare nails and no enhancements, unless you have booked a removal or fill.',
                    'Please arrive no more than 5 minutes before your scheduled appointment.',
                    'Every appointment includes a 15-minute grace period for late arrivals. If you are running behind, please reach out as soon as possible.',
                    'If your appointment date or time no longer works for you, please cancel and book a new appointment.',
                ],
            },
            {
                heading: 'Booking',
                items: [
                    'Your booked appointment reflects the standard cost of your services. Any additional nail art or charms will be added to your total once your service is complete.',
                    'You will not be charged more than your deposit until your service has been fully provided and marked complete.',
                    'I do not work over other technicians\' work. Please book a removal or have previous enhancements removed before your appointment.',
                    'Cash payments are welcome; however, all appointments must still be booked online with a card on file and any required deposit.',
                    'Card payments include a processing fee of 2.9% + $0.10 per transaction.',
                    'Bank and direct debit payments include a processing fee of 0.8% of the total amount.',
                ],
            },
            {
                heading: 'Cancellations',
                items: [
                    'Cancellations made at least 24 hours before your scheduled appointment are free, and your deposit will be refunded.',
                    'Deposits are non-refundable for cancellations made within 24 hours of your scheduled appointment. Please message me with any questions before cancelling, as exceptions will be considered.',
                    'No-shows will be charged the full cost of the scheduled service. Your deposit is applied toward this total, not charged in addition to it.',
                    'Arriving more than 15 minutes late is treated as a no-show / late cancellation and will be charged the full scheduled appointment amount, unless you contacted me beforehand regarding an emergency.',
                ],
            },
        ],
    },
    {
        id: 'ethics',
        title: 'Ethics',
        groups: [
            {
                heading: 'Health & Safety',
                items: [
                    'All reusable tools are cleaned and disinfected between every client, and the workstation is sanitized before each appointment.',
                    'Single-use items such as files and buffers are never reused between clients.',
                    'Only professional-grade products from reputable brands are used.',
                ],
            },
            {
                heading: 'Nail Health',
                items: [
                    'The health of your natural nails always comes first. I will recommend the service best suited to your nails, even if it differs from what was booked.',
                    'Services will not be performed on nails or skin showing signs of infection, fungus, or injury. In these cases, I may recommend seeing a medical professional.',
                ],
            },
            {
                heading: 'Honesty & Respect',
                items: [
                    'Pricing is transparent, and any additional costs will be discussed with you before they are added.',
                    'Every client is welcomed and treated with kindness and respect, regardless of background or identity.',
                    'Your comfort matters. Please let me know at any time if something feels uncomfortable during your service.',
                ],
            },
        ],
    },
];

function Policy() {
    const { hash } = useLocation();

    // Content renders after the initial load, so the browser's native hash scroll misses it.
    useEffect(() => {
        if (hash) {
            const target = document.getElementById(hash.slice(1));
            target?.querySelector('details')?.setAttribute('open', '');
            target?.scrollIntoView();
        }
    }, [hash]);

    return (
        <Page
            title="Policies & Ethics"
            subtitle="Everything you need to know before your visit"
            grid={false}
        >
            <div className={styles.sections}>
                {sections.map((section) => (
                    <section key={section.id} id={section.id} className={styles.card}>
                        <details className={styles.disclosure}>
                            <summary className={styles.summary}>
                                <h2 className={styles.title}>{section.title}</h2>
                            </summary>
                            <div className={styles.content}>
                                {section.intro && <p className={styles.intro}>{section.intro}</p>}
                                {section.groups.map((group) => (
                                    <div key={group.heading} className={styles.group}>
                                        <h3 className={styles.heading}>{group.heading}</h3>
                                        <ul className={styles.list}>
                                            {group.items.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </details>
                    </section>
                ))}
            </div>
        </Page>
    );
}

export default Policy;